/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, UncontrolledPagination, ITelemetryContent, getTelemetryObject } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { IRequestContext } from '@msdyn365-commerce/core';
import * as React from 'react';
import { getSimpleProductsAction, ProductInput, getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { IInvoiceDetailsViewProps } from '@msdyn365-commerce-modules/invoice-details/src/modules/invoice-details/./invoice-details';
import { IContext, IQueryResultSettings, SalesInvoiceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICocCInvoiceDetailsProps } from './coc-c-invoice-details.props.autogenerated';
import InvoiceDetailsPaymentSummary from './components/coc-c-invoice-details-payment-summary';
import { ICocCInvoiceDetailsData } from './coc-c-invoice-details.data';
import { InvoiceDetailsHeader } from './components/coc-c-invoice-details-header';
import { InvoiceDetailsProductDetailComponent } from './components/invoice-details-product-component';
import { IGetInvoiceDetailsRequest, IInvoiceDetailsEntity } from './../../customProxy/retailExtension/InvoiceDetailDataServiceEntities.g';
import { getInvoiceDetailsAsync } from '../../customProxy/retailExtension/InvoiceDetailDataActionExtension.g';

/**
 * TODO:These interfaces and class will be in a seprate file, due to time limitation we are keeping them here.
 */
export interface IInvoicePaymentDetails {
    totalDiscount?: number;
    subtotal?: number;
    totalCharges?: number;
    totalTax?: number;
    totalAmount?: number;
    currencyCode?: string;
}

export interface IInvoiceProductLine extends SalesInvoiceLine {
    primaryImageUrl?: string;
}

export interface SalesInvoiceCustom {
    RecordID?: number;
    SalesID?: string;
    InvoiceID?: string;
    TotalDiscount?: number;
    InvoiceSubTotal?: number;
    TotalCharges?: number;
    TotalTaxAmount?: number;
    ItemID?: string;
    ProductName?: string;
    FolioNumber?: string;
    SearchKey?: string;
    SearchKeyName?: string;
    Address?: string;
    CreatedBy?: string;
    CreatedDate?: string;
    OrignalSalesID?: string;
    RecID?: number;
    ExtensionProperties?: string[];
}

export interface SalesInvoiceArray {
    SalesInvoiceCustom?: SalesInvoiceCustom[];
}

export interface ProductComponetInput {
    primaryImageUrl?: string;
    altImageUrl?: string;
    productName?: string;
    searckKey?: string;
    FolioNumber?: string;
    SalesOrder?: string;
    CreatedDate?: Date;
    PlacedBy?: string;
    productPrice?: number;
    recordId: number;
}

export interface ProductHeaderInput {
    invoiceDate: Date | undefined;
    invoiceid: string;
    totalProductValue: number;
    //shipping: number;
    totalTaxes: number;
    invoiceTotal: number;
    props: IInvoiceDetailsViewProps;
}

export const InvoiceDetailsView: React.FC<IInvoiceDetailsViewProps & ICocCInvoiceDetailsProps<ICocCInvoiceDetailsData>> = (
    props: IInvoiceDetailsViewProps & ICocCInvoiceDetailsProps<ICocCInvoiceDetailsData>
) => {
    const [invoiceDetailsState, setInvoiceDetailsState] = React.useState<SimpleProduct[]>();
    const {
        header,
        payInvoiceError,
        resources: { invoiceSubTitle }
    } = props;

    const telemetryContent: ITelemetryContent = getTelemetryObject(
        props.context.request.telemetryPageName!,
        props.friendlyName,
        props.telemetry
    );
    const _pageType: string | undefined = props.context.request.urlTokens.pageType;

    const [arr, setArr] = React.useState<ProductComponetInput[]>([]);
    const [pagination, setPagination] = React.useState<React.ReactNode | undefined>();
    const [invoiceDetailHeader, setInvoiceDetailHeader] = React.useState<ProductHeaderInput>();
    const [invoiceDetailsCustom, setInvoiceDetailCustom] = React.useState<IInvoiceDetailsEntity[]>();
    const [dataExportInvoiceDetails, setInvoiceDetailCustom1] = React.useState<IInvoiceDetailsEntity[]>();
    const itemsPerPage = props.config.itemsPerPage;

    const getInvoiceDetails: IGetInvoiceDetailsRequest = {
        InvoiceId: props.context.request.query?.invoiceId
    };

    async function dataction(input: IGetInvoiceDetailsRequest, context: IContext, top: number): Promise<IInvoiceDetailsEntity[]> {
        const invoiceID: IGetInvoiceDetailsRequest = { InvoiceId: props.context.request.query?.invoiceId };
        const topItems: number = itemsPerPage ? itemsPerPage : 1000;
        const skip = Number(context.callerContext.requestContext.query?.skip);
        const queryResultSettingsValue: IQueryResultSettings = {
            Paging: { Top: topItems, Skip: skip || 0 }
        };
        const promise = await getInvoiceDetailsAsync({ callerContext: props.context.actionContext }, invoiceID, queryResultSettingsValue);
        return promise;
    }

    React.useEffect(() => {
        dataction(getInvoiceDetails, { callerContext: props.context.actionContext }, 1000)
            .then(res => {
                if (res === undefined || res === null) {
                    props.context.telemetry.error('No Response from Server');
                } else {
                    setInvoiceDetailCustom(res);
                }
            })
            .catch(error => {
                props.context.telemetry.error(`${error}`);
            });
    }, []);

    async function dataction1(
        input: IGetInvoiceDetailsRequest,
        context: IContext,
        itemsPerPage: number,
        skip: number
    ): Promise<IInvoiceDetailsEntity[]> {
        const invoiceID: IGetInvoiceDetailsRequest = { InvoiceId: input.InvoiceId }; // Assuming input is the parameter passed to the function
        const queryResultSettingsValue: IQueryResultSettings = {
            Paging: { Top: itemsPerPage, Skip: skip }
        };
        const promise = await getInvoiceDetailsAsync({ callerContext: props.context.actionContext }, invoiceID, queryResultSettingsValue); // Use context instead of props.context
        return promise;
    }

    React.useEffect(() => {
        const itemsPerPage = 1000; // Set the number of items per page
        const allRecords: IInvoiceDetailsEntity[] = [];

        async function fetchData() {
            let skip = 0;
            let hasMoreRecords = true;

            while (hasMoreRecords) {
                try {
                    const records = await dataction1(getInvoiceDetails, { callerContext: props.context.actionContext }, itemsPerPage, skip); // Pass props.context.actionContext instead of props.context

                    if (records.length === 0) {
                        hasMoreRecords = false;
                    } else {
                        allRecords.push(...records);
                        skip += itemsPerPage;
                    }
                } catch (error) {
                    props.context.telemetry.error(`${error}`);
                    break;
                }
            }

            // Process all records after fetching
            setInvoiceDetailCustom1(allRecords);
        }

        void fetchData();
    }, []);

    // console.log(
    //     'GetInvoiceDetails =>',
    //     dataExportInvoiceDetails?.map(item => item)
    // );
    React.useEffect(() => {
        const { channelId } = props.context.actionContext.requestContext.apiSettings;

        _getProducts(invoiceDetailsCustom!, channelId)
            .then(value => {
                //console.log('VALUE', value);
                setInvoiceDetailsState(value);
            })
            .catch(error => {
                //console.log('ERROR', error);
                props.context.telemetry.error(`${error}`);
            });
    }, [invoiceDetailsCustom]);

    React.useEffect(() => {
        if (invoiceDetailsCustom === undefined || invoiceDetailsCustom === null) {
            props.context.telemetry.error('Something Went Wrong.');
        } else {
            const invoice = props.data.invoice;
            const invoiceDetailHeaderPropBuilder: ProductHeaderInput = {
                invoiceDate: undefined,
                invoiceid: '',
                totalProductValue: 0,
                //shipping: 0,
                totalTaxes: 0,
                invoiceTotal: 0,
                props: props
            };
            invoiceDetailHeaderPropBuilder.invoiceTotal = invoice.result?.TotalAmount!;
            invoiceDetailHeaderPropBuilder.invoiceid = invoice.result?.Id!;
            invoiceDetailHeaderPropBuilder.totalProductValue = invoiceDetailsCustom[0].InvoiceSubTotal
                ? invoiceDetailsCustom[0].InvoiceSubTotal
                : 0;
            invoiceDetailHeaderPropBuilder.invoiceDate = invoice.result?.InvoiceDate;
            //invoiceDetailHeaderPropBuilder.shipping = 0;
            invoiceDetailHeaderPropBuilder.invoiceTotal = invoice.result?.TotalAmount!;
            invoiceDetailHeaderPropBuilder.totalTaxes = invoiceDetailsCustom[0].TotalTaxAmount ? invoiceDetailsCustom[0].TotalTaxAmount : 0;
            setInvoiceDetailHeader(invoiceDetailHeaderPropBuilder);

            void Promise.all([
                invoiceDetailsCustom.map(item => {
                    let imageURL;
                    //let Price;
                    const altImageURL = getFallbackImageUrl(item.ItemID, props.context.actionContext.requestContext.apiSettings);
                    //console.log('STATE', invoiceDetailsState);
                    invoiceDetailsState?.map(line => {
                        if (line.RecordId === item.ProductRecID) {
                            imageURL = line.PrimaryImageUrl;
                            //Price = line.Price;
                        }
                    });

                    const arrinput: ProductComponetInput = {
                        primaryImageUrl: imageURL,
                        altImageUrl: altImageURL,
                        productName: item.ProductName,
                        searckKey: item.SearchKey,
                        FolioNumber: item.FolioNumber,
                        SalesOrder: item.OrignalSalesID,
                        CreatedDate: item.CreatedDate,
                        PlacedBy: item.CreatedBy,
                        productPrice: item.LineAmount,
                        recordId: item.ProductRecID!
                    };
                    return arrinput;
                }),
                props.data.listPageState.then(listPageState => {
                    if (_pageType === 'Category') {
                        listPageState.pageType = 'Category';
                    } else {
                        listPageState.pageType = 'Search';
                    }

                    const pageNumber = Number(props.context.request.query?.skip || 0);
                    const totalProductCount = invoiceDetailsCustom[0].ExtensionProperties?.find(item => item.Key === 'TotalRecords')?.Value
                        ?.IntegerValue;
                    listPageState.totalProductCount = totalProductCount ? totalProductCount : itemsPerPage;
                    listPageState.currentPageNumber = pageNumber / (itemsPerPage ? itemsPerPage : 10) + 1;
                    listPageState.activeProducts = invoiceDetailsCustom!;
                })
            ]).then(arrinput => {
                setArr(arrinput[0]);
                setPagination(_getPagination());
            });
        }
    }, [invoiceDetailsCustom, invoiceDetailsState]);

    async function _getProducts(invoiceLines: IInvoiceDetailsEntity[], channelId?: number): Promise<SimpleProduct[]> {
        //console.log('INPUT', invoiceLines);

        const productInputs = invoiceLines.map(
            product => new ProductInput(product.ProductRecID!, props.context.request.apiSettings, channelId)
        );

        const products = await getSimpleProductsAction(productInputs, props.context.actionContext);
        return products;
    }

    const _renderHeader = (): JSX.Element => {
        return (
            <InvoiceDetailsHeader
                className={'msc-invoice-details'}
                id={`${header.props.className}__${props.data.invoice.result?.Id}`}
                data={invoiceDetailHeader!}
                properties={props}
            />
        );
    };

    const _renderPaymentDetails = (): JSX.Element => {
        return (
            <InvoiceDetailsPaymentSummary
                className={`msc-invoice-details__payment-details`}
                id={`msc-invoice-details__payment-details__${props.data.invoice.result?.Id}`}
                data={invoiceDetailHeader!}
                properties={props}
            />
        );
    };

    const _getPagination = (): React.ReactNode => {
        const { context, data, resources } = props;
        const listPageState = data && data.listPageState && data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const skipCount = Number(props.context.request.query?.skip!) || 0;
        const totalItems = listPageState?.totalProductCount;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={_renderPrev(previousText)}
                nextText={_renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={telemetryContent}
            />
        );
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    const _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    const _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    /**
     * Gets the current URL of the page from the request context.
     * @param requestContext The request context.
     */
    const getCurrentUrl = (requestContext: IRequestContext): URL => {
        if (MsDyn365.isBrowser) {
            return new URL(window.location.href);
        }

        // NOTE: Typing on requestURL is incorrect
        if (requestContext.url.requestUrl.href) {
            return new URL(requestContext.url.requestUrl.href);
        }
        return new URL(JSON.stringify(requestContext.url.requestUrl));
    };

    // Data export in PDF, CSV and Excel
    //const invoiceNumber = props.data.invoice.result?.Id;
    const invoiceMonth = props.data.invoice.result?.InvoiceDate;
    const invoiceTotal = invoiceDetailHeader && invoiceDetailHeader?.invoiceTotal;
    const totalTax = invoiceDetailHeader && invoiceDetailHeader?.totalTaxes;
    const totalProductValue = invoiceDetailHeader && invoiceDetailHeader?.totalProductValue;
    //const shipping = invoiceDetailHeader && invoiceDetailHeader?.shipping;
    //console.log('testing => ', arr);
    const downloadPDF = () => {
        const table1HTML = document.querySelector('#table1pdf')?.innerHTML || '';
        const table2HTML = document.querySelector('#table2pdf')?.innerHTML || '';
        // const { innerHTML } = document.querySelector('#table1pdf')!;
        const printWindow = window.open('', '_blank');
        printWindow &&
            printWindow.document.write(`
      <html>
        <head>
          <title>Invoice Details</title>
          <style>
            table tr td {padding:10px;}
            table {border-collapse:collapse;}
            table tr th {padding:10px;}
          </style>
        </head>
        <body>
        <h2 style='textCenter'>Invoice Details</h2>
        <table border="1">
        ${table1HTML}
      </table>
      <h4>Items included in this invoice </h4>
      <table border="1">
        ${table2HTML}
      </table>
        </body>
      </html>
    `);
        printWindow && printWindow.document.close();
        printWindow && printWindow.print();
    };

    const downloadCSV = (content: any, filename: any) => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const convertToCSV = (data: any[]) => {
        // Function to wrap a value in double quotes
        const wrapInDoubleQuotes = (value: any) => `"${value}"`;

        // Function to capitalize the first letter of a string
        const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

        // Get the header and capitalize the first letter of each heading
        const header = Object.keys(data[0])
            .map(capitalizeFirstLetter)
            .map(wrapInDoubleQuotes)
            .join(',');

        // Convert rows to CSV format
        const rows = data.map(item =>
            Object.values(item)
                .map(wrapInDoubleQuotes)
                .join(',')
        );

        // Combine header and rows
        return `${header}\n${rows.join('\n')}`;
    };

    const exportDataToCSV = () => {
        // Specify a filename
        const filename = 'invoice_details.csv';

        // Convert the data to CSV format
        const csvContent = convertToCSV(csvtData);

        // Download the CSV
        downloadCSV(csvContent, filename);
    };

    const csvtData = [
        {
            //'Invoice #': invoiceNumber,
            'Invoice Date': new Date(invoiceMonth!)
                .toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })
                .toString(),
            'Total Product Value': `$${totalProductValue?.toFixed(2)?.toString()}`,
            //Shipping: `$${shipping?.toFixed(2)?.toString()}`,
            'Total Taxes (GST)': `$${totalTax?.toFixed(2)?.toString()}`,
            'Invoice Total': `$${invoiceTotal?.toFixed(2)?.toString()}`
        },
        {
            orderNumber: '',
            invoiceMonth: '',
            totalProductValue: '',
            totalTaxes: '',
            invoiceTotal: '',
            placedBy: '',
            extra: ''
        },
        {
            orderNumber: 'Items included in this invoice',
            invoiceMonth: '',
            totalProductValue: '',
            totalTaxes: '',
            invoiceTotal: '',
            placedBy: '',
            extra: ''
        },
        {
            orderNumber: '',
            invoiceMonth: '',
            totalProductValue: '',
            totalTaxes: '',
            invoiceTotal: '',
            placedBy: '',
            extra: ''
        },
        {
            orderNumber: 'Sales Order #',
            invoiceMonth: 'Product',
            totalProductValue: 'Product Price',
            totalTaxes: 'Search Key',
            invoiceTotal: 'Folio Number',
            placedBy: 'Created Date',
            extra: 'Placed By'
        },
        ...(dataExportInvoiceDetails || []).map(product => ({
            orderNumber: product.OrignalSalesID?.toString(),
            invoiceMonth: product.ProductName?.toString(),
            totalProductValue: `$${product.LineAmount?.toFixed(2)?.toString()}`,
            totalTaxes: product.SearchKey?.replace(/\|\|/g, '').toString(),
            invoiceTotal: product.FolioNumber?.toString(),
            placedBy: new Date(product.CreatedDate!)
                .toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })
                .toString(),
            extra: product.CreatedBy?.toString()
        }))
        // Add more data as needed
    ];

    function exportToExcel() {
        // Data for the first table
        const data1 = [
            //['Invoice #', 'Invoice Date', 'Total Product Value', 'Shipping', 'Total Taxes (GST)', 'Invoice Total'],
            ['Invoice Date', 'Total Product Value', 'Total Taxes (GST)', 'Invoice Total'],
            [
                //invoiceNumber,
                new Date(invoiceMonth!).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }),
                `$${totalProductValue?.toFixed(2)}`,
                //`$${shipping?.toFixed(2)}`,
                `$${totalTax?.toFixed(2)}`,
                `$${invoiceTotal?.toFixed(2)}`
            ]
            // Add more rows as needed
        ];

        // Data for the second table
        const data2 = [
            ['Sales Order #', 'Product', 'Product Price', 'Search Key', 'Folio Number', 'Created Date', 'Placed By'],
            ...(dataExportInvoiceDetails || []).map(product => [
                product.OrignalSalesID?.toString(),
                product.ProductName?.toString(),
                `$${product.LineAmount?.toFixed(2)}`,
                product.SearchKey?.replace(/\|\|/g, '').toString(),
                product.FolioNumber,
                new Date(product.CreatedDate!)
                    .toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })
                    .toString(),
                product.CreatedBy?.toString()
            ])
            // Add more rows as needed
        ];

        // Combine data from both tables
        const combinedData = [
            ...data1,
            [''], // Empty row between tables
            ['Items included in this invoice'],
            [''], // Empty row between tables
            ...data2
        ];

        const tableContent = `<table style="border-collapse: collapse;"><tbody>${combinedData
            .map((row, rowIndex) => getTableRow(row, rowIndex))
            .join('')}</tbody></table>`;
        const dataUri = `data:application/vnd.ms-excel;charset=utf-8,${encodeURIComponent(tableContent)}`;

        const link = document.createElement('a');
        link.href = dataUri;
        link.download = 'invoice_details.xls'; // Use .xls extension for Excel
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    function getTableRow(row: any, rowIndex: any, isHeader = false) {
        return `<tr>${row
            .map(
                (cell: any, index: any) =>
                    `<${isHeader || rowIndex === 0 ? 'th' : 'td'} style="border: 1px solid #f7f7f7; ${getCellAlignment(rowIndex, index)}">${
                        isHeader || rowIndex === 0 || rowIndex === 3 || rowIndex === 5 ? '<b>' : ''
                    }${cell}${isHeader || rowIndex === 0 || rowIndex === 3 || rowIndex === 5 ? '</b>' : ''}</${
                        isHeader || rowIndex === 0 ? 'th' : 'td'
                    }>`
            )
            .join('')}</tr>`;
    }

    function createRange(start: number, end: number) {
        const range = [];
        for (let i = start; i <= end; i++) {
            range.push(i);
        }
        return range;
    }

    function getCellAlignment(rowIndex: any, columnIndex: any) {
        const conditions = [
            { row: [0, 3], col: [] },
            { row: [1], col: [1, 2, 3] },
            { row: createRange(6, 2500), col: [2] }
        ];

        for (const condition of conditions) {
            if (condition.row.includes(rowIndex) && condition.col.includes(columnIndex)) {
                return 'text-align: right;';
            }
        }

        return 'text-align: left;'; // Default to left-align for other cells
    }

    const handleSelectChange = async (event: { target: { value: any } }) => {
        const option = event.target.value;
        if (option === 'pdf') {
            downloadPDF();
        } else if (option === 'csv') {
            exportDataToCSV();
        } else if (option === 'xls') {
            exportToExcel();
        }
    };

    return (
        <div>
            <table id='table1pdf' className='hideTable'>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'left' }}>Invoice Date</th>
                        <th style={{ textAlign: 'left' }}>Total Product Value</th>
                        <th style={{ textAlign: 'left' }}>Total Taxes (GST)</th>
                        <th style={{ textAlign: 'left' }}>Invoice Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            {new Date(invoiceMonth!).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </td>
                        <td style={{ textAlign: 'right' }}>${totalProductValue?.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>${totalTax?.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>${invoiceTotal?.toFixed(2)}</td>
                    </tr>
                </tbody>
            </table>
            <table id='table2pdf' className='hideTable'>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'left' }}>Sales Order #</th>
                        <th style={{ textAlign: 'left' }}>Product Name</th>
                        <th style={{ textAlign: 'left' }}>Product Price</th>
                        <th style={{ textAlign: 'left' }}>Search Key</th>
                        <th style={{ textAlign: 'left' }}>Folio Number</th>
                        <th style={{ textAlign: 'left' }}>Created Date</th>
                        <th style={{ textAlign: 'left' }}>Placed By</th>
                    </tr>
                </thead>
                <tbody>
                    {dataExportInvoiceDetails &&
                        dataExportInvoiceDetails.map(product => (
                            <tr key={product.OrignalSalesID}>
                                <td>{product.OrignalSalesID}</td>
                                <td>{product.ProductName}</td>
                                <td style={{ textAlign: 'right' }}>${product.LineAmount?.toFixed(2)}</td>
                                <td>{product.SearchKey?.replace(/\|\|/g, '')}</td>
                                <td>{product.FolioNumber}</td>
                                <td>
                                    {new Date(product.CreatedDate!).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </td>
                                <td>{product.CreatedBy}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
            <Module {...props.moduleProps}>
                <select id='dropdown' onChange={handleSelectChange}>
                    <option value=''>Download</option>
                    <option value='pdf'>{props.resources.downloadPDF}</option>
                    <option value='xls'>{props.resources.downloadExcel}</option>
                    <option value='csv'>{props.resources.downloadCSV}</option>
                </select>
                {invoiceDetailHeader && _renderHeader()}
                {invoiceDetailHeader && _renderPaymentDetails()}
                <h2 className='sub-title'>{invoiceSubTitle}</h2>
                {arr.length > 0 ? <InvoiceDetailsProductDetailComponent data={arr} resources={props.resources} properties={props} /> : null}
                {payInvoiceError}
            </Module>
            {pagination}
        </div>
    );
};

export default InvoiceDetailsView;
